import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * The old container, kept as a compatibility layer while the views migrate to
 * `Section` + `Surface` in `ui/section.tsx`.
 *
 * It no longer draws a border or an internal header rule -- both were the
 * box-of-boxes grammar this redesign removed -- so a view that has not been
 * rewritten yet still sits correctly in the new world. Nothing new should
 * import from here; see `ui/section.tsx` for why the split exists.
 *
 * Still importing it, and listed as known drift in DESIGN.md: `routes/me.tsx`,
 * `routes/about.tsx`, `features/fleet/WorkerTable.tsx` and
 * `components/auth/AdminOnly.tsx`. The difference left is where the heading
 * sits: inside the surface here, on the page ground in `Section`.
 */
function Panel({ className, ...props }: React.ComponentProps<'section'>) {
  return (
    <section data-slot="panel" className={cn('dy-surface overflow-hidden', className)} {...props} />
  );
}

/** A panel's own header, now a heading with space under it rather than a rule. */
function PanelHead({
  title,
  meta,
  actions,
  hue,
  className,
}: {
  title: React.ReactNode;
  meta?: React.ReactNode;
  actions?: React.ReactNode;
  hue?: string;
  className?: string;
}) {
  return (
    <div
      data-slot="panel-head"
      className={cn('flex items-baseline gap-2.5 px-gutter pt-3.5 pb-2', className)}
    >
      <h2
        className="text-lg min-w-0 truncate font-semibold"
        style={hue ? { color: hue } : undefined}
      >
        {title}
      </h2>
      {meta ? <span className="text-fg-3 tnum text-xs">{meta}</span> : null}
      {actions ? <div className="ml-auto flex items-center gap-1.5">{actions}</div> : null}
    </div>
  );
}

/**
 * A group heading inside a list: sticky, so the group a row belongs to is still
 * named when you have scrolled into the middle of it.
 */
function GroupHead({
  label,
  count,
  tone,
  actions,
  className,
}: {
  label: string;
  count?: number;
  tone?: string;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      data-slot="group-head"
      className={cn(
        'bg-card/92 sticky top-0 z-[1] flex items-center gap-2 px-gutter pt-3 pb-1.5 backdrop-blur-[2px]',
        className,
      )}
    >
      <span className={cn('dy-eyebrow', tone)}>{label}</span>
      {count === undefined ? null : (
        <span className={cn('text-fg-3 tnum text-2xs', !actions && 'ml-auto')}>{count}</span>
      )}
      {actions ? <div className="ml-auto flex items-center gap-1">{actions}</div> : null}
    </div>
  );
}

/** A well: a recessed strip for a form, a summary or a reason field. */
function Well({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="well"
      className={cn('dy-inset mx-gutter mb-3 px-3 py-2.5', className)}
      {...props}
    />
  );
}

export { GroupHead, Panel, PanelHead, Well };
